/**
 * `/flows/:flow_name/edit` page — phase 33a; live edits since 2026-10-01.
 *
 * Form editor for a marketing flow's steps. Reads the flow via
 * `GET /api/flows/:flow_name` (the stored live edit when there is one, else
 * the shipped YAML, plus what may change), lets operators edit the per-step
 * wake times, actions, opt-out checks and message templates, then dispatches
 * `edit_marketing_flow` through the chokepoint (via `POST
 * /api/flows/:flow_name`). The save is a LIVE EDIT stored in the database:
 * members who enter the flow after it get the edited steps; members already
 * in it finish their current run on the version they started with. A loop
 * pass resolves the version current when it starts, so members in a loop get
 * the edit from their next repeat. "Restore shipped version" deletes the
 * live edit.
 *
 * Page structure:
 *   - Top: live-edit status (edited live / shipped version changed since /
 *     not running), then the flow header — name, trigger and exit events are
 *     READ-ONLY: code wires them when the receiver boots, so an edit there
 *     would show one thing and do another.
 *   - Body: ordered step list. Each step has: wake target + opt_out_check +
 *     action dropdown (the step actions the walkers can send, from the GET) +
 *     per-channel input template fields. A WhatsApp step is either an
 *     approved template or free text with an approved fallback template,
 *     both picked from the studio's catalog (`whatsapp_templates`). An email
 *     step has an "Email design" row: View shows the step as a member would
 *     get it — the flow as last SAVED, which the row says while there are
 *     unsaved changes — and Edit opens the HTML editor where it has an email
 *     to start from (`emailStepPreview` decides both, from the GET's `engine`
 *     and `designed_steps`).
 *   - Loop (2026-10-01; only on flows whose walker runs loop passes): repeat
 *     the last N steps forever or N times, optionally at a set time of day.
 *     The looped steps are bracketed in the step list, with a summary.
 *   - Footer: Save + Discard unsaved changes + Restore shipped version +
 *     Cancel.
 *
 * NO automated test coverage at phase 33a — per the post-phase-27
 * frontend-test-infra carve-out, React renderer files are visually
 * verified through Mario's Tier-2 playbook documented in the smoke
 * runbook.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { dispatchAction } from "../lib/dispatch";
import { supabase } from "../lib/supabase";
import { reportApiFailure } from "../lib/session-lost";
import { studioHeaders } from "../lib/studio-scope";
import { studioFormat } from "../lib/studio-tz";
import { invalidateFlows } from "../lib/resource-cache-invalidation";
import {
  emailStepPreview,
  flowStepTextBodyLabel,
  newFlowStepTextBody,
  previewDocument,
  previewFailureText,
  previewSubject,
  previewSubtitle,
  textToHtmlDocument,
  type EmailPreviewResponse,
} from "../lib/email-inventory";
import { EmailPreviewModal } from "../components/EmailPreviewModal";
import type { CatalogFlowLiveEdit } from "@layer/dashboard/types";

/**
 * The Meta-approved WhatsApp template each TRANSACTIONAL flow sends via its
 * `member_transactional_wa_*` sibling saga (view-only — Meta owns the wording).
 * A hand-kept copy of `MEMBER_TRANSACTIONAL_WA_TEMPLATE_NAMES` in
 * `packages/saga-runtime/src/sagas/member-transactional-wa.ts` (the source of
 * truth — this bundle cannot import it), held to it by
 * `apps/dashboard/tests/flow-editor-wa-companion.test.ts`, which parses THIS
 * literal: keep one entry per flow, two-space indented, with `name` /
 * `homeName` / `vars` as plain double-quoted strings. Home-session variants
 * are shown where the approved copy names a venue.
 */
const WA_COMPANION_TEMPLATE: Record<
  string,
  { readonly name: string; readonly homeName?: string; readonly vars: string }
> = {
  booking_confirmation: {
    name: "booking_confirmation",
    homeName: "home_booking_confirmation",
    vars: "{{1}} session date/time · {{2}} trainer",
  },
  booking_cancellation: {
    name: "member_booking_cancellation",
    vars: "{{1}} first name · {{2}} session date/time · {{3}} credit line",
  },
  booking_rescheduled: {
    name: "member_booking_reschedule",
    vars: "{{1}} first name · {{2}} new time · {{3}} trainer",
  },
  booking_trainer_changed: {
    name: "member_trainer_change",
    homeName: "member_trainer_change_home",
    vars: "{{1}} first name · {{2}} session date/time · {{3}} new trainer",
  },
  payment_receipt: {
    name: "member_payment_receipt_v3",
    // {{3}} is `purchasedItemPhrase`: a package by its name, a membership (its
    // first payment and every renewal) by its plan — "One Year membership".
    vars: "{{1}} first name · {{2}} amount · {{3}} what was bought (the package, or the membership plan)",
  },
  refund_processed: {
    name: "member_refund_v2",
    // {{3}} is `purchasedItemPhrase`, as for the receipt: a refunded package
    // by its name, a refunded membership payment by its plan.
    vars: "{{1}} first name · {{2}} amount · {{3}} what was refunded (the package, or the membership plan)",
  },
};

interface FlowStepAnchorShape {
  trigger_field: string;
  offset_hours: number;
}

interface FlowStepShape {
  delay_after_previous_hours: number;
  action: string;
  opt_out_check?: string;
  exit_events?: ReadonlyArray<string>;
  anchor?: FlowStepAnchorShape;
  input_template: Record<string, unknown>;
  /**
   * The @layer/email-templates HTML-layout reference. Read-only here (set by
   * the flow author, edited under the Email Templates page) — but it MUST be
   * carried through Save, or the rendered email downgrades to plaintext.
   */
  template?: string;
}

/**
 * `FlowDefinition.repeat` — the last `last_steps` steps run again, `times`
 * runs in all counting the first (`"infinite"` = until the member leaves the
 * flow), at `send_time` studio-local when set. Server rules: marketing-flows.md
 * "Loops" (`validateFlowRepeat`).
 */
interface FlowRepeatShape {
  last_steps: number;
  times: number | "infinite";
  send_time?: string;
  /** The reading the loop features, one per repeat in order, then round again. */
  articles?: FlowLoopArticleShape[];
}

/**
 * One `repeat.articles` entry (2026-10-01). A step in the loop shows the
 * current repeat's with `{{ article.title }}`, `{{ article.source }}` and
 * `{{ article.url }}`. Server rules: `validateFlowLoopArticles`.
 */
interface FlowLoopArticleShape {
  title: string;
  source: string;
  url: string;
}

/** The fields `{{ article.<field> }}` may name. */
const ARTICLE_FIELDS: ReadonlyArray<string> = ["title", "source", "url"];

/** Every `{{ article.<field> }}` field named anywhere in a step's template. */
function articleTokenFields(value: unknown): string[] {
  if (typeof value === "string") {
    return Array.from(
      value.matchAll(/\{\{\s*article\.([a-zA-Z0-9_]+)\s*\}\}/g),
      (m) => m[1] ?? "",
    );
  }
  if (Array.isArray(value)) return value.flatMap(articleTokenFields);
  if (value !== null && typeof value === "object") {
    return Object.values(value as Record<string, unknown>).flatMap(articleTokenFields);
  }
  return [];
}

interface FlowShape {
  name: string;
  trigger_event: string;
  exit_events: ReadonlyArray<string>;
  default_locale?: string;
  steps: FlowStepShape[];
  repeat?: FlowRepeatShape;
}

/** One entry of the studio's approved WhatsApp template catalog. */
interface WhatsAppCatalogTemplate {
  name: string;
  language_code: string;
  category: string;
  variable_count: number;
}

interface LoopLimits {
  min_cycle_hours: number;
  max_run_hours: number;
  max_times: number;
  max_articles: number;
}

interface FlowFetchResponse {
  flow: FlowShape;
  studio_slug: string;
  /** False for a flow whose message lives in code (on/off switch only). */
  live_editable: boolean;
  not_editable_reason: string | null;
  allowed_step_actions: ReadonlyArray<string>;
  live_edit: CatalogFlowLiveEdit | null;
  /**
   * Which engine runs the flow's steps — `drip` for a marketing flow,
   * `immediate` for a transactional one. Decides what previews an email step.
   */
  engine: "drip" | "immediate" | null;
  /** The step positions (1-based) with a bespoke email design in code. */
  designed_steps: ReadonlyArray<number>;
  /** Whether this flow's walker runs loop passes; only then may it loop. */
  loop_supported: boolean;
  loop_limits: LoopLimits;
  /** What the save checks every WhatsApp step against. */
  whatsapp_templates: ReadonlyArray<WhatsAppCatalogTemplate>;
  /**
   * When the flow asks, in words, for a flow whose schedule is set on
   * `/config` (2026-10-21: only `session_feedback_request`); null for the
   * rest. Optional for a response from a server older than the field.
   */
  cadence_summary?: string | null;
}

/** `POST /api/flows/:flow_name` response body. */
interface FlowSaveResponse {
  ok?: boolean;
  code?: string;
  message?: string;
  response?: {
    outcome?: "live_edit_saved" | "shipped_version_restored";
    design_warnings?: ReadonlyArray<string>;
  };
}

function formatSavedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return studioFormat({
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

/**
 * Phase 34 sub-decision F — per-event date-ISO field allow-list. Used
 * by the per-step anchor widget to populate the `trigger_field`
 * dropdown. MUST stay in sync with `MARKETING_FLOW_EVENT_FIELDS` in
 * `packages/canonical-data/src/l2-yaml.ts` (server-side validator).
 */
const MARKETING_FLOW_EVENT_FIELDS: Record<string, ReadonlyArray<string>> = {
  "member.signed_up": ["signed_up_at"],
  "member.first_purchased": ["first_purchased_at"],
  "member.purchased": ["paid_at"],
  "member.booking_made": ["starts_at", "ends_at"],
  "member.booking_cancelled": ["cancelled_at"],
  "member.session_completed": ["ended_at"],
  "member.profile_updated": ["updated_at"],
  "member.membership_started": ["started_at"],
  "member.membership_cancelled": ["cancelled_at"],
  "member.credit_granted": ["granted_at"],
  "member.refunded": ["refunded_at"],
  "member.stripe_customer_registered": ["registered_at", "signed_up_at"],
  "member.payment_failed": ["failed_at"],
  "member.membership_payment_issue": ["occurred_at"],
  "member.session_rated": ["rated_at"],
  "member.booking_rescheduled": [
    "new_starts_at",
    "new_ends_at",
    "old_starts_at",
    "old_ends_at",
  ],
  "member.tagged": ["tagged_at"],
  "member.untagged": ["untagged_at"],
  "member.booking_trainer_changed": ["starts_at"],
  "member.checkout_abandoned": ["abandoned_at"],
};

function uuidv7(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const t = BigInt(Date.now());
  bytes[0] = Number((t >> 40n) & 0xffn);
  bytes[1] = Number((t >> 32n) & 0xffn);
  bytes[2] = Number((t >> 24n) & 0xffn);
  bytes[3] = Number((t >> 16n) & 0xffn);
  bytes[4] = Number((t >> 8n) & 0xffn);
  bytes[5] = Number(t & 0xffn);
  bytes[6] = (bytes[6]! & 0x0f) | 0x70;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}

/** Who saved a live edit, for the status line (the stored actor string). */
function savedByLabel(actor: string): string {
  if (actor.startsWith("human:staff")) return "staff";
  if (actor.startsWith("system:cli")) return "the CLI";
  return actor;
}

/** `720` → "30 days"; `36` → "36 hours". */
function formatHours(hours: number): string {
  if (hours % 24 === 0) {
    const days = hours / 24;
    return `${String(days)} ${days === 1 ? "day" : "days"}`;
  }
  return `${String(hours)} ${hours === 1 ? "hour" : "hours"}`;
}

/** 0-based index of the first looped step, or null when the flow has no loop. */
function firstLoopedIndex(flow: FlowShape): number | null {
  const r = flow.repeat;
  if (r === undefined || r.times === 1) return null;
  return Math.max(flow.steps.length - r.last_steps, 0);
}

/** The loop in a sentence, or null when there is none. */
function loopSummary(flow: FlowShape): string | null {
  const first = firstLoopedIndex(flow);
  const r = flow.repeat;
  if (first === null || r === undefined) return null;
  const n = flow.steps.length;
  const looped = flow.steps.slice(first);
  const cycle = looped.reduce((sum, st) => sum + st.delay_after_previous_hours, 0);
  const range =
    first === n - 1
      ? `Step ${String(n)} repeats`
      : `Steps ${String(first + 1)}–${String(n)} repeat`;
  const how =
    r.times === "infinite"
      ? "for as long as the member stays in the flow"
      : `${String(r.times)} times in all, counting the first`;
  const at = r.send_time !== undefined ? `, sent at ${r.send_time} studio time` : "";
  const back =
    first === n - 1
      ? ""
      : ` After step ${String(n)}, step ${String(first + 1)} comes round again ${formatHours(looped[0]!.delay_after_previous_hours)} later.`;
  const articles = r.articles ?? [];
  const reading =
    articles.length === 0
      ? ""
      : ` Each repeat features the next article — ${articles
          .map((a, i) => `${String(i + 1)}. ${a.source || "(no publication)"}`)
          .join(", ")} — then round again.`;
  return `${range} every ${formatHours(cycle)}, ${how}${at}.${back}${reading}`;
}

/**
 * What the save would refuse about articles — a mirror of the server's
 * `validateFlowLoopArticles` + `validateFlowArticleTokens`. Checked whether
 * or not the flow loops: a step outside a running loop has no article to show.
 */
function articleIssues(flow: FlowShape): string[] {
  const first = firstLoopedIndex(flow) ?? flow.steps.length;
  const articles = flow.repeat?.articles ?? [];
  const issues: string[] = [];
  flow.steps.forEach((st, i) => {
    const fields = articleTokenFields(st.input_template);
    if (fields.length === 0) return;
    const n = String(i + 1);
    const unknown = fields.find((f) => !ARTICLE_FIELDS.includes(f));
    if (unknown !== undefined) {
      issues.push(
        `Step ${n} uses {{ article.${unknown} }} — an article has a title, a source and a url.`,
      );
    } else if (i < first) {
      issues.push(
        `Step ${n} shows an article, but only the steps in a repeating loop feature one — this step runs once, with no repeat to pick an article for it.`,
      );
    } else if (articles.length === 0) {
      issues.push(
        `Step ${n} shows an article, but the loop lists none — add the articles below.`,
      );
    }
  });
  articles.forEach((a, j) => {
    const k = String(j + 1);
    if (a.title.trim() === "" || a.source.trim() === "") {
      issues.push(`Article ${k} needs a title and the publication's name.`);
    }
    if (!/^https:\/\/[^\s]+$/.test(a.url)) {
      issues.push(`Article ${k}'s link must be a full https:// address.`);
    }
  });
  return issues;
}

/**
 * What the save would refuse about the loop — a mirror of the server's
 * `validateFlowRepeat` so the editor says it before Save. The server stays
 * the authority: its message is shown if this misses anything.
 */
function loopIssues(flow: FlowShape, limits: LoopLimits): string[] {
  const first = firstLoopedIndex(flow);
  const r = flow.repeat;
  if (first === null || r === undefined) return [];
  const issues: string[] = [];
  const looped = flow.steps.slice(first);
  looped.forEach((st, j) => {
    const n = first + j + 1;
    if (st.anchor !== undefined) {
      issues.push(
        `Step ${String(n)} is anchored to the trigger event, which happens once — a step in the loop needs a delay instead.`,
      );
    } else if (r.send_time !== undefined && st.delay_after_previous_hours % 24 !== 0) {
      issues.push(
        `Step ${String(n)} waits ${formatHours(st.delay_after_previous_hours)}, which is not whole days, so it cannot land at ${r.send_time}.`,
      );
    }
  });
  const cycle = looped.reduce((sum, st) => sum + st.delay_after_previous_hours, 0);
  if (cycle < limits.min_cycle_hours) {
    issues.push(
      `The loop would come round every ${formatHours(cycle)}; it must be at least ${formatHours(limits.min_cycle_hours)}, so nobody hears from a loop more than once a week. Lengthen the delays of the steps in the loop.`,
    );
  }
  return issues;
}

/**
 * `steps` after removing step `removed`, with the loop kept on the same
 * steps: removing a step inside the loop shrinks it (and removing its only
 * step ends it); removing one before it leaves it as it is.
 */
function repeatAfterRemove(
  flow: FlowShape,
  removed: number,
): FlowRepeatShape | undefined {
  const r = flow.repeat;
  if (r === undefined) return undefined;
  const first = Math.max(flow.steps.length - r.last_steps, 0);
  if (removed < first) return r;
  return r.last_steps > 1 ? { ...r, last_steps: r.last_steps - 1 } : undefined;
}

type FlowEditMeta = Omit<FlowFetchResponse, "flow">;

export function FlowEdit(): JSX.Element {
  const { flow_name } = useParams<{ flow_name: string }>();
  const navigate = useNavigate();
  const [flow, setFlow] = useState<FlowShape | null>(null);
  const [original, setOriginal] = useState<FlowShape | null>(null);
  const [meta, setMeta] = useState<FlowEditMeta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [designWarnings, setDesignWarnings] = useState<ReadonlyArray<string>>(
    [],
  );

  const applyFetched = useCallback((resp: FlowFetchResponse) => {
    const { flow: fetched, ...rest } = resp;
    setFlow(structuredClone(fetched));
    setOriginal(structuredClone(fetched));
    setMeta(rest);
  }, []);

  useEffect(() => {
    if (flow_name === undefined) {
      setError("Missing flow_name in URL");
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get<FlowFetchResponse>(`/api/flows/${flow_name}`)
      .then((flowResp) => {
        if (cancelled) return;
        applyFetched(flowResp);
        setError(null);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        if (e instanceof ApiError) {
          setError(`api_error:${e.status}${e.code ? `:${e.code}` : ""}`);
        } else {
          setError(e.message);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [flow_name, applyFetched]);

  // The step actions the walkers can send (`allowed_step_actions` from the
  // GET). A step already carrying another action keeps it visible.
  const sendActionOptions = useMemo<string[]>(() => {
    const allowed = meta?.allowed_step_actions ?? [
      "send_email",
      "send_whatsapp_message",
    ];
    return [...allowed].sort();
  }, [meta]);

  /**
   * POST a save or a restore through `edit_marketing_flow`, then re-read the
   * flow so the live-edit status shows what is stored now.
   */
  const postFlowEdit = useCallback(
    async (payload: { flow: FlowShape } | { reset: true }): Promise<void> => {
      if (flow_name === undefined) return;
      setSaving(true);
      setStatusMsg(null);
      setError(null);
      setDesignWarnings([]);
      try {
        const session = await supabase.auth.getSession();
        const token = session.data.session?.access_token ?? "";
        const res = await fetch(`/api/flows/${flow_name}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            ...studioHeaders(),
          },
          body: JSON.stringify({ request_id: uuidv7(), ...payload }),
        });
        const text = await res.text();
        let body: FlowSaveResponse;
        try {
          body = text.length > 0 ? (JSON.parse(text) as FlowSaveResponse) : {};
        } catch {
          body = {};
        }
        if (!res.ok) reportApiFailure(res.status, body.code ?? null);
        if (!res.ok || body.ok !== true) {
          setError(
            `save_failed (HTTP ${res.status}${body.code ? `, code=${body.code}` : ""}): ${body.message ?? text}`,
          );
          return;
        }
        const outcome = body.response?.outcome;
        const loopNote =
          meta?.loop_supported === true
            ? " Members in a loop get it from their next repeat."
            : "";
        setStatusMsg(
          outcome === "shipped_version_restored"
            ? `Shipped version restored. Members who enter the flow from now on get the version in git.${loopNote}`
            : `Saved live. Members who enter the flow from now on get these steps; members already in it finish their current run on the version they started with.${loopNote}`,
        );
        setDesignWarnings(body.response?.design_warnings ?? []);
        // `/flows` and the catalog carry the live-edit badges.
        invalidateFlows();
        applyFetched(
          await api.get<FlowFetchResponse>(`/api/flows/${flow_name}`),
        );
      } catch (e) {
        setError(`save_failed: ${(e as Error).message}`);
      } finally {
        setSaving(false);
      }
      // Silence the unused dispatch import — kept for future direct-dispatch
      // paths; the save uses raw fetch so the route runs without relying on
      // the generic dispatch helper's stable URL shape.
      void dispatchAction;
    },
    [flow_name, applyFetched, meta],
  );

  const handleSave = useCallback(async () => {
    if (flow === null) return;
    await postFlowEdit({ flow });
  }, [flow, postFlowEdit]);

  const handleRestore = useCallback(async () => {
    if (
      !window.confirm(
        meta?.loop_supported === true
          ? "Restore the shipped version? The live edit is deleted, and members who enter the flow from now on get the version in git. Members already in the flow finish their current run as it is; members in a loop get the version in git from their next repeat."
          : "Restore the shipped version? The live edit is deleted, and members who enter the flow from now on get the version in git. Members already in the flow are not affected.",
      )
    ) {
      return;
    }
    await postFlowEdit({ reset: true });
  }, [postFlowEdit, meta]);

  const handleDiscard = useCallback(() => {
    if (original === null) return;
    setFlow(structuredClone(original));
    setDesignWarnings([]);
    setStatusMsg("Unsaved changes discarded");
  }, [original]);

  if (loading) {
    return (
      <div className="page-flow-edit">
        <h1>Edit flow: {flow_name}</h1>
        <p>Loading…</p>
      </div>
    );
  }
  if (error !== null && flow === null) {
    return (
      <div className="page-flow-edit">
        <h1>Edit flow: {flow_name}</h1>
        <div className="tag tag-error">Error: {error}</div>
        <p>
          <button onClick={() => navigate("/flows")}>Back to /flows</button>
        </p>
      </div>
    );
  }
  if (flow === null || meta === null) return <div>No flow loaded.</div>;

  const editable = meta.live_editable;
  const liveEdit = meta.live_edit;
  const unsaved = JSON.stringify(flow) !== JSON.stringify(original);
  const loopFirst = firstLoopedIndex(flow);

  return (
    <div className="page-flow-edit">
      <div>
        <h1>Edit flow: {flow.name}</h1>
        <p className="subtle">
          Studio: <code>{meta.studio_slug}</code> · shipped in git at{" "}
          <code>
            verticals/ems/studios/{meta.studio_slug}/flows/{flow.name}.yaml
          </code>
          . A save here is a live edit: it is stored in the database and
          overrides that file until you restore the shipped version.
        </p>
        {!editable && (
          <div className="tag tag-warn flow-edit-notice">
            <strong>Not editable here.</strong>{" "}
            {meta.not_editable_reason ??
              "This flow's message is written in code."}{" "}
            Turn it on or off from <code>/flows</code>.
          </div>
        )}
        {meta.cadence_summary != null && (
          <div
            className="tag flow-edit-notice"
            data-testid="flow-edit-cadence"
          >
            <strong>When it asks:</strong> {meta.cadence_summary}. Set on{" "}
            <Link
              to={`/config/${encodeURIComponent(meta.studio_slug)}?section=marketing-flows`}
            >
              Config → Marketing flows
            </Link>
            .
          </div>
        )}
        {liveEdit !== null && (
          <div className="tag flow-edit-notice">
            <strong>Edited live</strong> — saved{" "}
            {formatSavedAt(liveEdit.updated_at)} by{" "}
            {savedByLabel(liveEdit.updated_by_actor)}. It replaces the shipped
            version for members who entered the flow after that save.
          </div>
        )}
        {liveEdit !== null && liveEdit.shipped_changed && (
          <div className="tag tag-warn flow-edit-notice">
            <strong>Shipped version changed since this edit.</strong> A deploy
            updated this flow's file after the live edit was saved. The live
            edit still runs; restore the shipped version to use the new file
            instead.
          </div>
        )}
        {liveEdit !== null && liveEdit.not_running_reasons.length > 0 && (
          <div className="tag tag-error flow-edit-notice">
            <strong>This live edit is not running</strong> — the shipped
            version runs instead, because:{" "}
            {liveEdit.not_running_reasons.join("; ")}. Save a corrected
            version or restore the shipped one.
          </div>
        )}
        {error !== null && (
          <div className="tag tag-error flow-edit-notice">Error: {error}</div>
        )}
        {statusMsg !== null && (
          <div className="tag tag-success flow-edit-notice">{statusMsg}</div>
        )}
        {designWarnings.map((w) => (
          <div key={w} className="tag tag-warn flow-edit-notice">
            {w}
          </div>
        ))}

        <fieldset>
          <legend>Flow header</legend>
          <div className="form-row">
            <label>Name</label>
            <input type="text" value={flow.name} readOnly />
          </div>
          <div className="form-row">
            <label>Trigger event</label>
            <input type="text" value={flow.trigger_event} readOnly />
            <small>
              Set in code: the receiver wires what starts this flow when it
              boots, so it cannot be changed here.
            </small>
          </div>
          <div className="form-row">
            <label>Exit events</label>
            <div>
              {flow.exit_events.length === 0 ? (
                <span className="subtle">none listed</span>
              ) : (
                flow.exit_events.map((ev) => (
                  <code key={ev} className="tag">
                    {ev}
                  </code>
                ))
              )}
            </div>
            <small>
              For reference only: what ends this flow is checked in code at
              each step, so this list cannot be changed here.
            </small>
          </div>
        </fieldset>

        <fieldset disabled={!editable}>
          <legend>Steps ({flow.steps.length})</legend>
          {flow.steps.map((step, idx) => (
            <StepEditor
              key={idx}
              index={idx}
              step={step}
              flowName={flow.name}
              engine={meta.engine}
              designedSteps={meta.designed_steps}
              unsaved={unsaved}
              loopLabel={
                loopFirst === null || idx < loopFirst
                  ? null
                  : idx === loopFirst
                    ? loopFirst === flow.steps.length - 1
                      ? "↻ Repeats"
                      : `↻ Loop: steps ${String(loopFirst + 1)}–${String(flow.steps.length)} repeat`
                    : idx === flow.steps.length - 1
                      ? `↻ Then back to step ${String(loopFirst + 1)}`
                      : "↻ In the loop"
              }
              whatsAppTemplates={meta.whatsapp_templates}
              sendActions={
                sendActionOptions.includes(step.action)
                  ? sendActionOptions
                  : [...sendActionOptions, step.action]
              }
              triggerEvent={flow.trigger_event}
              onChange={(updated) => {
                const next = flow.steps.slice();
                next[idx] = updated;
                setFlow({ ...flow, steps: next });
              }}
              onRemove={() => {
                const next = flow.steps.slice();
                next.splice(idx, 1);
                const { repeat: _repeat, ...rest } = flow;
                void _repeat;
                const repeat = repeatAfterRemove(flow, idx);
                setFlow({
                  ...rest,
                  steps: next,
                  ...(repeat !== undefined ? { repeat } : {}),
                });
              }}
              onMoveUp={() => {
                if (idx === 0) return;
                const next = flow.steps.slice();
                const [a, b] = [next[idx - 1]!, next[idx]!];
                next[idx - 1] = b;
                next[idx] = a;
                setFlow({ ...flow, steps: next });
              }}
              onMoveDown={() => {
                if (idx >= flow.steps.length - 1) return;
                const next = flow.steps.slice();
                const [a, b] = [next[idx]!, next[idx + 1]!];
                next[idx] = b;
                next[idx + 1] = a;
                setFlow({ ...flow, steps: next });
              }}
            />
          ))}
          <p>
            <button
              onClick={() => {
                const next = flow.steps.slice();
                // A flow whose steps honour opt-outs refuses a step without
                // one, so the new step takes the last step's key.
                const optOut = [...flow.steps]
                  .reverse()
                  .find((st) => st.opt_out_check !== undefined)?.opt_out_check;
                const action = sendActionOptions[0] ?? "send_email";
                next.push({
                  delay_after_previous_hours: 24,
                  action,
                  ...(optOut !== undefined ? { opt_out_check: optOut } : {}),
                  input_template: {
                    subject: "",
                    // A marketing email keeps its unsubscribe link (2026-10-23).
                    text_body: newFlowStepTextBody({
                      action,
                      ...(optOut !== undefined ? { opt_out_check: optOut } : {}),
                    }),
                  },
                });
                // The loop is always the last steps, so a step added at the
                // end joins it — keep the loop starting where it did.
                setFlow({
                  ...flow,
                  steps: next,
                  ...(flow.repeat !== undefined
                    ? { repeat: { ...flow.repeat, last_steps: flow.repeat.last_steps + 1 } }
                    : {}),
                });
              }}
            >
              + Add step
            </button>
            {loopFirst !== null && (
              <small className="subtle">
                {" "}
                A new step joins the end of the loop; move it up to put it
                before the loop.
              </small>
            )}
          </p>
        </fieldset>

        {meta.loop_supported && (
          <LoopEditor
            flow={flow}
            limits={meta.loop_limits}
            disabled={!editable}
            onChange={(repeat) => {
              const { repeat: _old, ...rest } = flow;
              void _old;
              setFlow(repeat === undefined ? rest : { ...rest, repeat });
            }}
          />
        )}

        {WA_COMPANION_TEMPLATE[flow.name] !== undefined && (
          <fieldset>
            <legend>WhatsApp companion (view-only)</legend>
            <div style={{ fontSize: 13, color: "var(--c-ink-41)", lineHeight: 1.6 }}>
              When this flow is on, the same event also sends a Meta-approved
              WhatsApp template to members who acted outside their own chat (via
              the <code>member_transactional_wa</code> saga, riding this same
              toggle). Its wording is managed in WhatsApp Manager — editing it
              there requires re-approval by Meta, so it is not editable here.
              <div style={{ marginTop: 10 }}>
                <strong>Template:</strong>{" "}
                <code>{WA_COMPANION_TEMPLATE[flow.name]!.name}</code>
                {WA_COMPANION_TEMPLATE[flow.name]!.homeName !== undefined && (
                  <>
                    {" "}
                    (home-session variant{" "}
                    <code>{WA_COMPANION_TEMPLATE[flow.name]!.homeName}</code>)
                  </>
                )}
                <br />
                <strong>Variables:</strong>{" "}
                {WA_COMPANION_TEMPLATE[flow.name]!.vars}
              </div>
            </div>
          </fieldset>
        )}

        <p className="subtle">
          Saving applies to members who enter this flow after you save.
          Members already in it finish their current run on the version they
          started with.
          {meta.loop_supported &&
            " Members in a loop get the saved version from their next repeat."}
        </p>
        <div className="form-row" style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <button onClick={handleSave} disabled={saving || !editable || !unsaved}>
            {saving ? "Saving…" : "Save"}
          </button>
          <button onClick={handleDiscard} disabled={saving || !unsaved}>
            Discard unsaved changes
          </button>
          {liveEdit !== null && (
            <button onClick={handleRestore} disabled={saving}>
              Restore shipped version
            </button>
          )}
          <button onClick={() => navigate("/flows")} disabled={saving}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

interface StepEditorProps {
  index: number;
  step: FlowStepShape;
  flowName: string;
  engine: FlowFetchResponse["engine"];
  designedSteps: ReadonlyArray<number>;
  /**
   * The editor holds changes not yet saved. View shows the flow as it runs,
   * so it says when that is not what is on screen.
   */
  unsaved: boolean;
  /** The loop bracket's label on this step; null = not in the loop. */
  loopLabel: string | null;
  whatsAppTemplates: ReadonlyArray<WhatsAppCatalogTemplate>;
  sendActions: ReadonlyArray<string>;
  triggerEvent: string;
  onChange: (next: FlowStepShape) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function migrateInputTemplate(
  fromAction: string,
  toAction: string,
  current: Record<string, unknown>,
): Record<string, unknown> {
  if (fromAction === toAction) return current;
  if (toAction === "send_whatsapp_message") {
    const carriedBody =
      typeof current["body"] === "string" && current["body"]
        ? current["body"]
        : typeof current["text_body"] === "string"
          ? current["text_body"]
          : "";
    return { body: carriedBody };
  }
  if (toAction === "send_email") {
    const carriedText =
      typeof current["text_body"] === "string" && current["text_body"]
        ? current["text_body"]
        : typeof current["body"] === "string"
          ? current["body"]
          : "";
    const out: Record<string, unknown> = {
      subject: typeof current["subject"] === "string" ? current["subject"] : "",
      text_body: carriedText,
    };
    if (typeof current["html_body"] === "string" && current["html_body"]) {
      out["html_body"] = current["html_body"];
    }
    return out;
  }
  return current;
}

function StepEditor({
  index,
  step,
  flowName,
  engine,
  designedSteps,
  unsaved,
  loopLabel,
  whatsAppTemplates,
  sendActions,
  triggerEvent,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: StepEditorProps): JSX.Element {
  const navigate = useNavigate();
  const input = step.input_template;
  const isEmail = step.action === "send_email";
  const isWhatsApp = step.action === "send_whatsapp_message";
  const emailPreview = emailStepPreview({
    flowName,
    engine,
    designedSteps,
    step,
    index,
  });
  const emailKind = emailPreview?.kind ?? null;
  const [preview, setPreview] = useState<{
    readonly html: string;
    readonly subtitle: string;
    readonly subject?: string;
  } | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  // The server previews the flow as it runs: with changes not yet saved, what
  // comes back is the saved step at this position, and the popup says so.
  const onView = useCallback(async () => {
    if (emailKind === null) return;
    setViewLoading(true);
    try {
      const r = await api.get<EmailPreviewResponse>(
        `/api/email-templates/${encodeURIComponent(emailKind)}/preview`,
      );
      const subject = previewSubject(r);
      setPreview({
        html: previewDocument(r),
        subtitle: previewSubtitle(r, { unsavedFlowChanges: unsaved }),
        ...(subject !== undefined ? { subject } : {}),
      });
    } catch (e) {
      setPreview({
        html: textToHtmlDocument(
          e instanceof ApiError
            ? previewFailureText(e, { unsavedFlowChanges: unsaved })
            : (e as Error).message,
        ),
        subtitle: "This email could not be shown.",
      });
    } finally {
      setViewLoading(false);
    }
  }, [emailKind, unsaved]);
  // Phase 34 sub-decision F — wake target widget. Two modes:
  //   - "relative" → uses delay_after_previous_hours, no anchor.
  //   - "anchor"   → uses {trigger_field, offset_hours}; delay is reset to 0.
  const wakeMode: "relative" | "anchor" =
    step.anchor !== undefined ? "anchor" : "relative";
  const anchorFields: ReadonlyArray<string> =
    MARKETING_FLOW_EVENT_FIELDS[triggerEvent] ?? [];
  return (
    <div className={loopLabel !== null ? "flow-step flow-step--looped" : "flow-step"}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
          marginBottom: 16,
        }}
      >
        <strong>
          Step {index + 1}
          {loopLabel !== null && (
            <span className="flow-step-loop-label">{loopLabel}</span>
          )}
        </strong>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={onMoveUp}>↑</button>
          <button onClick={onMoveDown}>↓</button>
          <button onClick={onRemove}>Remove</button>
        </div>
      </div>
      <div className="form-row">
        <label>Wake target</label>
        <select
          value={wakeMode}
          onChange={(e) => {
            const next = e.target.value as "relative" | "anchor";
            if (next === "anchor") {
              // Switch to anchor mode — preserve delay as a hint of
              // intent (becomes the offset's initial value when
              // there's a sensible default). Otherwise default to
              // first allowed field + 0 offset.
              const defaultField = anchorFields[0] ?? "starts_at";
              onChange({
                ...step,
                delay_after_previous_hours: 0,
                anchor: { trigger_field: defaultField, offset_hours: 0 },
              });
            } else {
              // Switch to relative mode — drop the anchor; preserve
              // delay (0 by default).
              const { anchor, ...rest } = step;
              void anchor;
              onChange({ ...rest });
            }
          }}
        >
          <option value="relative">Relative (delay after previous step)</option>
          <option value="anchor">Anchor (relative to trigger event field)</option>
        </select>
      </div>
      {wakeMode === "relative" && (
        <div className="form-row">
          <label>Delay (hours)</label>
          <input
            type="number"
            min={0}
            max={720}
            value={step.delay_after_previous_hours}
            onChange={(e) =>
              onChange({
                ...step,
                delay_after_previous_hours:
                  Number.parseInt(e.target.value, 10) || 0,
              })
            }
          />
        </div>
      )}
      {wakeMode === "anchor" && step.anchor !== undefined && (
        <>
          <div className="form-row">
            <label>Anchor trigger field</label>
            <select
              value={step.anchor.trigger_field}
              onChange={(e) =>
                onChange({
                  ...step,
                  anchor: {
                    trigger_field: e.target.value,
                    offset_hours: step.anchor!.offset_hours,
                  },
                })
              }
            >
              {anchorFields.length === 0 ? (
                <option value="">
                  (No date fields available for trigger '{triggerEvent}')
                </option>
              ) : (
                anchorFields.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))
              )}
            </select>
            <small className="subtle">
              Wake-time pinned to <code>{triggerEvent}</code>.
              <code>{step.anchor.trigger_field}</code>
            </small>
          </div>
          <div className="form-row">
            <label>Offset (hours; signed: negative = before, positive = after)</label>
            <input
              type="number"
              min={-720}
              max={720}
              value={step.anchor.offset_hours}
              onChange={(e) =>
                onChange({
                  ...step,
                  anchor: {
                    trigger_field: step.anchor!.trigger_field,
                    offset_hours: Number.parseInt(e.target.value, 10) || 0,
                  },
                })
              }
            />
          </div>
        </>
      )}
      <div className="form-row">
        <label>Action</label>
        <select
          value={step.action}
          onChange={(e) =>
            onChange({
              ...step,
              action: e.target.value,
              input_template: migrateInputTemplate(
                step.action,
                e.target.value,
                step.input_template,
              ),
            })
          }
        >
          {sendActions.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
      </div>
      <div className="form-row">
        <label>Opt-out check (key in members.metadata.flow_opt_outs)</label>
        <input
          type="text"
          value={step.opt_out_check ?? ""}
          placeholder="e.g. welcome_emails"
          onChange={(e) => {
            const v = e.target.value;
            if (v.length === 0) {
              const { opt_out_check, ...rest } = step;
              void opt_out_check;
              onChange({ ...rest });
            } else {
              onChange({ ...step, opt_out_check: v });
            }
          }}
        />
      </div>
      {isEmail && (
        <>
          <div className="form-row">
            <label>Subject</label>
            <input
              type="text"
              value={String(input["subject"] ?? "")}
              onChange={(e) =>
                onChange({
                  ...step,
                  input_template: { ...input, subject: e.target.value },
                })
              }
            />
          </div>
          <div className="form-row">
            <label>{flowStepTextBodyLabel(step)}</label>
            <textarea
              value={String(input["text_body"] ?? "")}
              rows={8}
              onChange={(e) =>
                onChange({
                  ...step,
                  input_template: { ...input, text_body: e.target.value },
                })
              }
            />
          </div>
          <div className="form-row">
            <label>Email design</label>
            {emailPreview !== null ? (
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <code>{emailPreview.kind}</code>
                <button type="button" onClick={onView} disabled={viewLoading}>
                  {viewLoading ? "Loading…" : "View"}
                </button>
                {emailPreview.htmlEditable ? (
                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        `/email-templates/${encodeURIComponent(emailPreview.kind)}/edit`,
                      )
                    }
                  >
                    Edit
                  </button>
                ) : null}
                <small className="subtle">
                  {emailPreview.htmlEditable ? (
                    <>
                      The branded HTML email — <strong>View</strong> it as
                      members get it, or <strong>Edit</strong> its full HTML
                      (live on the next send; the subject + plaintext above
                      stay as the fallback twin).
                    </>
                  ) : emailPreview.layout === "generic" ? (
                    <>
                      No design of its own: the subject and text above go out
                      in the shared layout. <strong>View</strong> shows it as
                      members get it.
                    </>
                  ) : emailPreview.layout === "plain_html" ? (
                    <>
                      No layout: the subject and text above go out as a plain
                      email, its links tappable and its unsubscribe address
                      the one word &ldquo;Unsubscribe&rdquo;.{" "}
                      <strong>View</strong> shows it as members get it.
                    </>
                  ) : (
                    <>
                      No HTML design set for this step — it sends plaintext
                      only. <strong>View</strong> shows it as members get it.
                    </>
                  )}
                  {unsaved ? (
                    <>
                      {" "}
                      View shows the flow as last saved — save to see your
                      changes.
                    </>
                  ) : null}
                </small>
              </div>
            ) : (
              <small className="subtle">
                No HTML design set for this step — it sends plaintext only.
              </small>
            )}
          </div>
        </>
      )}
      {isWhatsApp && (
        <>
          {step.template !== undefined && step.template.length > 0 && (
            <div className="form-row">
              <label>WhatsApp template (view-only)</label>
              <small className="subtle">
                <code>{step.template}</code> — a Meta-approved template. Its
                wording is managed in WhatsApp Manager (edit + re-submit for
                approval there), not here; the body below is the fallback /
                variable content.
              </small>
            </div>
          )}
          <WhatsAppStepFields
            input={input}
            templates={whatsAppTemplates}
            onChange={(next) => onChange({ ...step, input_template: next })}
          />
        </>
      )}
      {!isEmail && !isWhatsApp && (
        <div className="form-row">
          <label>Raw input template (JSON)</label>
          <textarea
            value={JSON.stringify(input, null, 2)}
            rows={6}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
                  onChange({ ...step, input_template: parsed });
                }
              } catch {
                // Don't propagate parse errors live; user has to fix JSON.
              }
            }}
          />
        </div>
      )}
      {preview !== null && (
        <EmailPreviewModal
          title={`Preview · ${emailKind ?? "email"}`}
          subtitle={preview.subtitle}
          {...(preview.subject !== undefined ? { subject: preview.subject } : {})}
          html={preview.html}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}

/**
 * A Meta template descriptor on a WhatsApp step: `input_template.template`
 * (a template send) or `input_template.window_fallback_template` (what a
 * free-text send becomes outside the member's 24-hour window). Unknown keys —
 * a template's own `buttons` parameters — ride along untouched.
 */
interface TemplateDescriptor {
  name: string;
  language_code: string;
  variables: string[];
  [key: string]: unknown;
}

function readTemplateDescriptor(raw: unknown): TemplateDescriptor | null {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  return {
    ...r,
    name: typeof r["name"] === "string" ? r["name"] : "",
    language_code: typeof r["language_code"] === "string" ? r["language_code"] : "",
    variables: Array.isArray(r["variables"])
      ? r["variables"].map((v) => (typeof v === "string" ? v : String(v)))
      : [],
  };
}

/**
 * A WhatsApp step's message. Meta delivers free text only inside the
 * member's 24-hour window, so a step is EITHER an approved template OR free
 * text that names the approved template to send instead outside the window
 * (`checkFlowLiveEdit` refuses anything else).
 */
function WhatsAppStepFields({
  input,
  templates,
  onChange,
}: {
  input: Record<string, unknown>;
  templates: ReadonlyArray<WhatsAppCatalogTemplate>;
  onChange: (next: Record<string, unknown>) => void;
}): JSX.Element {
  const template = readTemplateDescriptor(input["template"]);
  const fallback = readTemplateDescriptor(input["window_fallback_template"]);
  const mode: "template" | "text" = template !== null ? "template" : "text";
  const blank: TemplateDescriptor = { name: "", language_code: "", variables: [] };
  return (
    <>
      <div className="form-row">
        <label>Message</label>
        <select
          value={mode}
          onChange={(e) => {
            if (e.target.value === "template") {
              // The fallback, when there is one, becomes the template.
              onChange({ template: fallback ?? blank });
            } else {
              onChange({ body: "", window_fallback_template: template ?? blank });
            }
          }}
        >
          <option value="template">An approved template</option>
          <option value="text">
            Free text, with an approved template outside the 24-hour window
          </option>
        </select>
        <small>
          WhatsApp delivers free text only within 24 hours of the member's
          last message; outside that window only a Meta-approved template is
          delivered.
        </small>
      </div>
      {mode === "template" ? (
        <TemplateFields
          label="Template"
          descriptor={template ?? blank}
          templates={templates}
          onChange={(d) => onChange({ ...input, template: d })}
        />
      ) : (
        <>
          <div className="form-row">
            <label>Body (WhatsApp text)</label>
            <textarea
              value={String(input["body"] ?? "")}
              rows={6}
              onChange={(e) => onChange({ ...input, body: e.target.value })}
            />
          </div>
          <TemplateFields
            label="Outside the 24-hour window, send"
            descriptor={fallback ?? blank}
            templates={templates}
            onChange={(d) => onChange({ ...input, window_fallback_template: d })}
          />
        </>
      )}
    </>
  );
}

/** Pick an approved template and fill in its variables. */
function TemplateFields({
  label,
  descriptor,
  templates,
  onChange,
}: {
  label: string;
  descriptor: TemplateDescriptor;
  templates: ReadonlyArray<WhatsAppCatalogTemplate>;
  onChange: (next: TemplateDescriptor) => void;
}): JSX.Element {
  const listed = templates.find((t) => t.name === descriptor.name);
  const buttons = Array.isArray(descriptor["buttons"])
    ? (descriptor["buttons"] as ReadonlyArray<{ sub_type?: unknown; variables?: unknown }>)
    : [];
  return (
    <>
      <div className="form-row">
        <label>{label}</label>
        {templates.length > 0 ? (
          <select
            value={descriptor.name}
            onChange={(e) => {
              const t = templates.find((x) => x.name === e.target.value);
              if (t === undefined) return;
              // Another template: its variables keep what fits, and the old
              // template's button parameters do not carry over.
              onChange({
                name: t.name,
                language_code: t.language_code,
                variables: Array.from(
                  { length: t.variable_count },
                  (_, i) => descriptor.variables[i] ?? "",
                ),
              });
            }}
          >
            {descriptor.name === "" && (
              <option value="" disabled>
                Pick an approved template…
              </option>
            )}
            {descriptor.name !== "" && listed === undefined && (
              <option value={descriptor.name}>
                {descriptor.name} (not in the studio's catalog)
              </option>
            )}
            {templates.map((t) => (
              <option key={t.name} value={t.name}>
                {t.name} · {t.category} · {t.variable_count}{" "}
                {t.variable_count === 1 ? "variable" : "variables"}
              </option>
            ))}
          </select>
        ) : (
          <input type="text" value={descriptor.name} readOnly />
        )}
        <small>
          Meta owns the wording (WhatsApp Manager). Fill in its variables
          below — <code>{"{{ member.first_name }}"}</code> and the other flow
          fields work here too.
        </small>
      </div>
      {descriptor.variables.map((v, i) => (
        <div className="form-row" key={i}>
          <label>Variable {`{{${String(i + 1)}}}`}</label>
          <input
            type="text"
            value={v}
            onChange={(e) => {
              const variables = descriptor.variables.slice();
              variables[i] = e.target.value;
              onChange({ ...descriptor, variables });
            }}
          />
        </div>
      ))}
      {buttons.length > 0 && (
        <div className="form-row">
          <label>Button parameters (view-only)</label>
          <small>
            {buttons
              .map(
                (b, i) =>
                  `${String(i + 1)}: ${String(b.sub_type)} ${JSON.stringify(b.variables)}`,
              )
              .join(" · ")}
          </small>
        </div>
      )}
    </>
  );
}

/**
 * "Times in all": the text is the field's own while it is typed, and is
 * clamped to 2..max and committed when it loses focus or on Enter. Clamping
 * each keystroke made a number like 12 impossible to type (the "1" snapped
 * to 2), and a cleared field snapped straight back.
 */
function LoopTimesInput({
  value,
  max,
  onCommit,
}: {
  value: number;
  max: number;
  onCommit: (times: number) => void;
}): JSX.Element {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);
  const commit = (): void => {
    const typed = Number.parseInt(draft, 10);
    const times = Number.isNaN(typed) ? value : Math.min(Math.max(typed, 2), max);
    setDraft(String(times));
    if (times !== value) onCommit(times);
  };
  return (
    <input
      type="number"
      min={2}
      max={max}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
      }}
    />
  );
}

/**
 * The flow's loop: repeat the last N steps, forever or N times in all, at
 * an optional time of day. Shown only on flows whose walker runs loop passes
 * (`loop_supported`); the server refuses a loop anywhere else.
 */
function LoopEditor({
  flow,
  limits,
  disabled,
  onChange,
}: {
  flow: FlowShape;
  limits: LoopLimits;
  disabled: boolean;
  onChange: (repeat: FlowRepeatShape | undefined) => void;
}): JSX.Element {
  const r = flow.repeat;
  const on = r !== undefined && r.times !== 1;
  const n = flow.steps.length;
  const summary = loopSummary(flow);
  const issues = [...loopIssues(flow, limits), ...articleIssues(flow)];
  const articles = r?.articles ?? [];
  const setArticles = (next: FlowLoopArticleShape[]): void => {
    if (r === undefined) return;
    const { articles: _a, ...rest } = r;
    void _a;
    onChange(next.length > 0 ? { ...rest, articles: next } : rest);
  };
  const editArticle = (i: number, patch: Partial<FlowLoopArticleShape>): void => {
    setArticles(articles.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  };
  const moveArticle = (i: number, by: -1 | 1): void => {
    const j = i + by;
    if (j < 0 || j >= articles.length) return;
    const next = articles.slice();
    [next[i], next[j]] = [next[j]!, next[i]!];
    setArticles(next);
  };
  return (
    <fieldset disabled={disabled}>
      <legend>Loop</legend>
      <div className="form-row">
        <label>Repeat</label>
        <select
          value={on ? "on" : "off"}
          onChange={(e) =>
            onChange(
              e.target.value === "on"
                ? { last_steps: 1, times: "infinite" }
                : undefined,
            )
          }
        >
          <option value="off">Off — the flow ends after its last step</option>
          <option value="on">Repeat the last steps</option>
        </select>
      </div>
      {on && r !== undefined && (
        <>
          <div className="form-row">
            <label>Steps in the loop</label>
            <select
              value={String(r.last_steps)}
              onChange={(e) =>
                onChange({ ...r, last_steps: Number.parseInt(e.target.value, 10) })
              }
            >
              {Array.from({ length: n }, (_, i) => i + 1).map((k) => (
                <option key={k} value={String(k)}>
                  {k === 1
                    ? `The last step (step ${String(n)})`
                    : `The last ${String(k)} steps (steps ${String(n - k + 1)}–${String(n)})`}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label>How often</label>
            <select
              value={r.times === "infinite" ? "infinite" : "count"}
              onChange={(e) =>
                onChange({
                  ...r,
                  times: e.target.value === "infinite" ? "infinite" : 2,
                })
              }
            >
              <option value="infinite">
                Until the member leaves the flow
              </option>
              <option value="count">A set number of times</option>
            </select>
          </div>
          {r.times !== "infinite" && (
            <div className="form-row">
              <label>Times in all, counting the first</label>
              <LoopTimesInput
                value={r.times}
                max={limits.max_times}
                onCommit={(times) => onChange({ ...r, times })}
              />
            </div>
          )}
          <div className="form-row">
            <label>Send time (studio time)</label>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={r.send_time !== undefined}
                onChange={(e) => {
                  const { send_time: _t, ...rest } = r;
                  void _t;
                  onChange(e.target.checked ? { ...rest, send_time: "10:00" } : rest);
                }}
              />
              {r.send_time !== undefined ? (
                <input
                  type="time"
                  step={60}
                  value={r.send_time}
                  onChange={(e) => {
                    if (/^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(e.target.value)) {
                      onChange({ ...r, send_time: e.target.value });
                    }
                  }}
                />
              ) : (
                <span className="subtle">
                  Off — each repeat lands at the same time of day as the first.
                </span>
              )}
            </div>
            <small>
              With a send time, the delays of the steps in the loop must be
              whole days.
            </small>
          </div>
          <div className="form-row">
            <label>Articles</label>
            <div>
              {articles.map((a, i) => (
                <div key={i} className="flow-article">
                  <div className="flow-article-head">
                    <strong>Repeat {String(i + 1)}</strong>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        type="button"
                        onClick={() => moveArticle(i, -1)}
                        disabled={i === 0}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => moveArticle(i, 1)}
                        disabled={i === articles.length - 1}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        onClick={() => setArticles(articles.filter((_, j) => j !== i))}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  <input
                    aria-label={`Article ${String(i + 1)} publication`}
                    placeholder="Publication — e.g. The Times"
                    value={a.source}
                    onChange={(e) => editArticle(i, { source: e.target.value })}
                  />
                  <input
                    aria-label={`Article ${String(i + 1)} headline`}
                    placeholder="Headline"
                    value={a.title}
                    onChange={(e) => editArticle(i, { title: e.target.value })}
                  />
                  <input
                    aria-label={`Article ${String(i + 1)} link`}
                    type="url"
                    placeholder="https://…"
                    value={a.url}
                    onChange={(e) => editArticle(i, { url: e.target.value.trim() })}
                  />
                </div>
              ))}
              <button
                type="button"
                disabled={articles.length >= limits.max_articles}
                onClick={() =>
                  setArticles([...articles, { title: "", source: "", url: "" }])
                }
              >
                + Add article
              </button>
              <small className="flow-article-help">
                Each repeat features the next article in this order, then the
                list comes round again. In the steps in the loop, write{" "}
                <code>{"{{ article.title }}"}</code>,{" "}
                <code>{"{{ article.source }}"}</code> and{" "}
                <code>{"{{ article.url }}"}</code> where it should appear. A
                change reaches every member from their next repeat.
              </small>
            </div>
          </div>
          {summary !== null && (
            <div className="tag flow-edit-notice">
              <strong>{summary}</strong> It stops when the member leaves the
              flow (its exit rules) or opts out, when the flow is switched off,
              or when the loop is turned off here. Each repeat uses the version
              of the flow saved when it starts.
            </div>
          )}
          {issues.map((issue) => (
            <div key={issue} className="tag tag-warn flow-edit-notice">
              {issue}
            </div>
          ))}
        </>
      )}
      {!on &&
        issues.map((issue) => (
          <div key={issue} className="tag tag-warn flow-edit-notice">
            {issue}
          </div>
        ))}
    </fieldset>
  );
}
